/**
 * The single source of truth for the color theme.
 *
 * The top bar and the settings panel both switch the theme, and each used to
 * hold its own useState — so toggling one left the other's control showing the
 * opposite of what was on screen. This external store keeps them in step: every
 * setter writes the DOM attribute and localStorage and notifies every reader,
 * and a storage event applies the new value in the tabs that did not make it. `index.html` and main.tsx set the
 * attribute before first paint, so `current()` is always meaningful.
 */
import { useSyncExternalStore } from 'react'

export type Theme = 'dark' | 'light'

const KEY = 'kimene.theme'
const listeners = new Set<() => void>()

/**
 * What the browser paints its own chrome with, per theme.
 *
 * These are `--k-bg` for each theme, and they are duplicated here because the
 * `<meta>` tag takes a colour rather than a custom property — a stylesheet
 * variable is not resolvable from meta content. Read from the computed style
 * instead of hardcoded, so the two cannot drift: the token stays the source of
 * truth and this only mirrors it.
 */
function paintBrowserChrome(): void {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')
  if (!meta) return
  const bg = getComputedStyle(document.documentElement).getPropertyValue('--k-bg').trim()
  if (bg) meta.content = bg
}

function current(): Theme {
  return document.documentElement.dataset.theme === 'light' ? 'light' : 'dark'
}

/** Apply a theme and notify every reader (this tab) — storage events cover others. */
export function setTheme(next: Theme) {
  document.documentElement.dataset.theme = next
  localStorage.setItem(KEY, next)
  // The address bar on Android and the task-switcher card on both platforms are
  // painted from this, and an installed window's title bar with it. Left alone,
  // a console switched to the light theme kept a black strip above it that no
  // amount of looking at the stylesheet explained.
  paintBrowserChrome()
  listeners.forEach((l) => l())
}

/** Paints the browser chrome from whatever theme is already on the document.
 *  Called once at boot, after `index.html` and main.tsx have set the attribute
 *  but before anything has had cause to change it. */
export function syncBrowserChrome(): void {
  paintBrowserChrome()
}

function subscribe(cb: () => void) {
  listeners.add(cb)
  const onStorage = (e: StorageEvent) => {
    if (e.key !== KEY) return
    // The attribute is applied here, not just re-read. Another tab's write
    // changes this tab's localStorage and nothing else, and `current()` reads
    // the *attribute* — so a handler that only notified would re-read a value
    // that had not moved and conclude nothing had happened. The browser chrome
    // is repainted for the same reason: the meta tag follows the attribute, and
    // leaving it behind is the black strip described in `setTheme`.
    const next: Theme = e.newValue === 'light' ? 'light' : 'dark'
    document.documentElement.dataset.theme = next
    paintBrowserChrome()
    cb()
  }
  window.addEventListener('storage', onStorage)
  return () => {
    listeners.delete(cb)
    window.removeEventListener('storage', onStorage)
  }
}

/** Reads the current theme and re-renders when it changes anywhere. */
export function useTheme(): [Theme, (t: Theme) => void] {
  const theme = useSyncExternalStore(subscribe, current, (): Theme => 'dark')
  return [theme, setTheme]
}
